import { useEffect, useRef } from 'react';

/**
 * Fire `onOpen` / `onClose` exactly on the rising / falling edge of `isOpen`.
 *
 * Centralises the open/close-transition bookkeeping shared by the settings detail
 * panels (snapshot-on-open, restore-on-cancel). Pass memoised handlers — they may
 * re-run on unrelated renders, but only act on an actual transition.
 */
export function usePanelOpenClose(
	isOpen: boolean,
	{ onOpen, onClose }: { onOpen?: () => void; onClose?: () => void }
): void {
	const wasOpenRef = useRef(false);

	useEffect(() => {
		const justOpened = isOpen && !wasOpenRef.current;
		const justClosed = !isOpen && wasOpenRef.current;
		wasOpenRef.current = isOpen;

		if (justOpened) onOpen?.();
		else if (justClosed) onClose?.();
	}, [isOpen, onOpen, onClose]);
}
